import UserAvatar from "@/src/components/ui-kit/user-avatar";
import { useAuth } from "@/src/contexts/auth.context";
import { StoredAccount } from "@/src/services/api/account-store";
import { AddCircle, CloseCircle, TickCircle } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Pressable,
  Text,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import { useTheme } from "@/src/styles/theme-provider";
import Toast from "react-native-toast-message";

type Props = Readonly<{
  visible: boolean;
  onClose: () => void;
}>;

type AccountRowProps = Readonly<{
  account: StoredAccount;
  isActive: boolean;
  onPress: (account: StoredAccount) => void;
}>;
function AccountRow({ account, isActive, onPress }: AccountRowProps) {
  const { themeVars } = useTheme();
  const { t } = useTranslation();

  const avatarLetter = account?.email?.[0]?.toUpperCase() ?? "";
  const rowClassName = `flex-row items-center gap-2.5 p-2.5 rounded-xl ${
    isActive ? "bg-primary/10 hover:bg-primary-2" : "active:bg-ash hover:bg-ash-3"
  }`;

  return (
    <Pressable onPress={() => onPress(account)} className={rowClassName}>
      <View className="rounded-full overflow-hidden">
        <UserAvatar firstName={avatarLetter} size={36} />
      </View>

      <View className="flex-1">
        <Text
          className="font-semibold text-gray-dark text-[13px]"
          numberOfLines={1}
        >
          {account?.email}
        </Text>
        {isActive && (
          <Text className="text-[11px] text-primary mt-0.5">
            {t("LABEL_ACTIVE_ACCOUNT")}
          </Text>
        )}
      </View>

      {isActive && (
        <TickCircle size={20} color={themeVars["--color-primary-default"] as string} variant="Bold" />
      )}
    </Pressable>
  );
}

export default function AccountSwitcherSheet({ visible, onClose }: Props) {
  const { themeVars, varStyle } = useTheme();
  const { t } = useTranslation();
  const { accounts, user, switchAccount, addAccount } = useAuth();

  const handleSwitchAccount = async (account: StoredAccount) => {
    if (account?.userId === user?.itemId) return;
    try {
      await switchAccount(account.userId);
      onClose();
    } catch (error: any) {
      const errorCode =
        error?.response?.data?.error ?? error?.response?.data?.message?.error;
      // The account was removed from the switcher in this case (see switchAccount).
      const isInactive = errorCode === "user_inactive_or_not_verified";
      onClose();
      Toast.show({
        type: "error",
        text1: isInactive
          ? t("ERROR_ACCOUNT_INACTIVE_OR_UNVERIFIED")
          : t("ERROR_SWITCH_ACCOUNT_FAILED"),
      });
    }
  };

  const handleAddAccount = () => {
    try {
      addAccount();
      onClose();
    } catch (error) {
      Toast.show({ type: "error", text1: t("ERROR_ADD_ACCOUNT_FAILED") });
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={varStyle} className="flex-1 bg-black/40 justify-end items-center">
          <TouchableWithoutFeedback>
            <View className="bg-chalk rounded-t-3xl pb-3 w-full md:w-[460px]">
              <View className="w-8 h-1 bg-ash-3 rounded-full self-center mt-3" />

              <View className="flex-row items-center justify-between px-4 pt-3 pb-2.5">
                <Text className="text-sm font-semibold text-gray-dark">
                  {t("LABEL_SWITCH_ACCOUNT")}
                </Text>
                <Pressable onPress={onClose} hitSlop={8} className="active:opacity-70 hover:bg-ash-3 hover:rounded-full">
                  <CloseCircle size={24} color={themeVars["--color-primary-default"] as string} />
                </Pressable>
              </View>

              <View className="h-px bg-ash mx-4 mb-1.5" />

              <View className="px-2 gap-0.5">
                {accounts.map((account) => {
                  const isActive = account?.userId === user?.itemId;
                  return (
                    <AccountRow
                      key={account.userId}
                      account={account}
                      isActive={isActive}
                      onPress={handleSwitchAccount}
                    />
                  );
                })}
              </View>

              <View className="h-px bg-ash mx-4 my-2.5" />

              <View className="px-2">
                <Pressable
                  onPress={handleAddAccount}
                  className="flex-row items-center gap-2.5 p-2.5 rounded-xl active:bg-ash hover:bg-ash-3"
                >
                  <View className="w-9 h-9 items-center justify-center">
                    <AddCircle size={24} color={themeVars["--color-primary-default"] as string} />
                  </View>
                  <Text className="text-[13px] font-medium text-slate">
                    {t("BTN_ADD_ACCOUNT")}
                  </Text>
                </Pressable>
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
}
